import { formatDateToDDMMYYYY } from "../../utils/dateUtils";
import React, { useEffect, useState } from 'react';
import axios from '../../config/axiosConfig';
import { MdArrowBack, MdEdit, MdDelete, MdOpenInNew } from 'react-icons/md';

// Reusable Read-Only Field Component
const DetailField = ({ label, value }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <div className="mt-1 block w-full rounded-md border-gray-200 bg-gray-50 border p-2 text-gray-900 min-h-[42px]">
            {value || '-'}
        </div>
    </div>
);

const MemberDetails = ({ memberId, onBack, onEdit, onDelete }) => {
    const [member, setMember] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchMember = async () => {
            try {
                const response = await axios.get(`nai-members/members/${memberId}/`);
                setMember(response.data);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching member details:", error);
                setLoading(false);
            }
        };

        if (memberId) {
            fetchMember();
        }
    }, [memberId]);

    const handleDeleteClick = () => {
        if (window.confirm("Are you sure you want to delete this member?")) {
            onDelete(memberId);
        }
    };

    // Helper to calculate ages if missing
    const getCalculatedAges = (member) => {
        let { registered_on, valid_till, member_age, membership_age } = member;
        
        // If values exist, return them
        if (member_age && membership_age) {
            return { member_age, membership_age };
        }

        const currentYear = new Date().getFullYear();
        let calculatedMembershipAge = membership_age;
        let calculatedMemberAge = member_age;

        if (registered_on) {
            const regParts = registered_on.split('-'); // Backend sends YYYY-MM-DD usually, or DD/MM/YYYY
            // Need to handle format. AddMember sends date string. 
            // Only calculate if we can parse the year.
            // Let's assume standard format or try to parse
            
            // Try to parse year from registered_on
            // If it comes from backend as YYYY-MM-DD
            let regYear = null;
            if (registered_on.includes('-')) {
                 regYear = parseInt(registered_on.split('-')[0]);
            } else if (registered_on.includes('/')) {
                 regYear = parseInt(registered_on.split('/')[2]);
            }
            
            if (regYear) {
                // Calculate Membership Age (Tenure)
                if (!membership_age) {
                    const diff = currentYear - regYear;
                    calculatedMembershipAge = diff >= 0 ? `${diff} Years` : '-';
                }

                // Calculate Member Age (Duration)
                if (!member_age && valid_till) {
                    let validYear = null;
                    if (valid_till.includes('-')) {
                        validYear = parseInt(valid_till.split('-')[0]);
                    } else if (valid_till.includes('/')) {
                        validYear = parseInt(valid_till.split('/')[2]);
                    }

                    if (validYear) {
                        const diff = validYear - regYear;
                        calculatedMemberAge = diff >= 0 ? `${diff} Years` : '-';
                    }
                }
            }
        }
        
        return { 
            member_age: calculatedMemberAge || '-', 
            membership_age: calculatedMembershipAge || '-' 
        };
    };

    const { member_age: displayMemberAge, membership_age: displayMembershipAge } = member ? getCalculatedAges(member) : {};

    // Helper to format payment type
    const formatPaymentType = (type) => {
        if (type === 'registration') return 'Membership Fee';
        if (type === 'leadership') return 'Leadership Fee';
        if (type === 'renewal') return 'Renewal Fee';
        return type;
    };

    if (loading) return <div className="p-6">Loading details...</div>;
    if (!member) return <div className="p-6">Member not found.</div>;

    return (
        <div className="bg-white rounded-lg shadow p-6">
            {/* Header: Back + Actions */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-900">
                    <MdArrowBack className="mr-2" /> Back to Members
                </button>
                
                <div className="flex gap-3">
                    <a 
                        href={`/${member.slug ? (member.slug.startsWith('member/') ? member.slug : `member/${member.slug}`) : `member/${member.id}`}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors"
                    >
                        <MdOpenInNew className="mr-2" /> Show Profile
                    </a>
                    <button 
                        onClick={() => onEdit(memberId)}
                        className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
                    >
                        <MdEdit className="mr-2" /> Edit Member
                    </button>
                    <button 
                        onClick={handleDeleteClick}
                        className="flex items-center bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors"
                    >
                        <MdDelete className="mr-2" /> Delete Member
                    </button>
                </div>
            </div>

            <div className="flex justify-between items-start mb-6 border-b pb-4">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">
                        {member.member_name} <span className="text-sm font-normal text-gray-500">({member.member_id})</span>
                    </h2>
                </div>
                <div className="flex-shrink-0 ml-4">
                     {member.picture ? (
                        <img src={member.picture} alt="Profile" className="h-32 w-32 object-cover rounded-md border" />
                     ) : (
                        <div className="h-32 w-32 bg-gray-100 flex items-center justify-center text-gray-400 border rounded-md">
                            No Image
                        </div>
                     )}
                </div>
            </div>

            <div className="space-y-8 max-w-6xl">
                
                {/* 0. Leader Details (Conditional) */}
                {((member.member_type || '').toUpperCase() === 'LEADER') && (
                    <section>
                        <h3 className="text-lg uppercase  font-bold text-blue-700 mb-4 border-b pb-2">Leader Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <DetailField label="Leadership Wing" value={member.leadership_wing} />
                            <DetailField label="Leadership Place" value={member.leadership_place} />
                            <DetailField label="Leadership Designation" value={member.leadership_designation} />
                        </div>
                    </section>
                )}

                {/* 1. Basic Information */}
                <section>
                    <h3 className="text-lg font-bold uppercase text-blue-700 mb-4 border-b pb-2">Basic Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <DetailField label="Member Name" value={member.member_name} />
                        <DetailField label="NAI Member ID" value={member.member_id} />
                        <DetailField label="DMCR Member Code" value={member.member_code} />
                        
                        <DetailField label="Member Type" value={member.member_type} />
                        <DetailField label="Designation / Role" value={member.member_type_choice} />
                        <div className="flex flex-col">
                            <label className="block text-sm font-medium text-gray-700">Status</label>
                            <div className="mt-1">
                                {(() => {
                                    const isExpired = member.valid_till && new Date(member.valid_till) < new Date().setHours(0, 0, 0, 0);
                                    const status = isExpired ? 'EXPIRED' : (member.member_status || '').toUpperCase();
                                    return (
                                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 
                                            status === 'EXPIRED' ? 'bg-red-100 text-red-800' :
                                            status === 'SUSPENDED' ? 'bg-yellow-100 text-yellow-800' :
                                            status === 'DIED' ? 'bg-orange-100 text-orange-800' :
                                            status === 'DISMISSED' ? 'bg-red-200 text-red-900' :
                                            'bg-gray-100 text-gray-800'
                                        }`}>
                                            {status}
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </section>

                {/* 2. Registration & Dates */}
                <section>
                    <h3 className="text-lg font-bold uppercase text-blue-700 mb-4 border-b pb-2">Registration Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <DetailField label="Registered On" value={formatDateToDDMMYYYY(member.registered_on)} />
                        <DetailField label="Valid Till" value={formatDateToDDMMYYYY(member.valid_till)} />
                        <DetailField label="Last Renewed On" value={formatDateToDDMMYYYY(member.last_renewed_on)} />
                        <DetailField label="Renewal Status" value={member.renewal} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-4">
                        <DetailField label="Member Age (Duration)" value={displayMemberAge} />
                        <DetailField label="Membership Age (Tenure)" value={displayMembershipAge} />
                    </div>
                </section>

                {/* 3. Contact Details */}
                <section>
                    <h3 className="text-lg font-bold uppercase text-blue-700 mb-4 border-b pb-2">Contact Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <DetailField label="Mobile Number" value={member.mobile} />
                        <DetailField label="WhatsApp Number" value={member.whatsapp_no} />
                        <DetailField label="Email" value={member.email} />
                        <div className="col-span-1 md:col-span-3">
                           <DetailField label="Address" value={member.member_address} />
                        </div>
                    </div>
                </section>

                {/* 4. Location Details */}
                <section>
                    <h3 className="text-lg font-bold uppercase text-blue-700 mb-4 border-b pb-2">Location Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <DetailField label="State" value={member.state} />
                        <DetailField label="District" value={member.district} />
                        <DetailField label="City" value={member.city} />
                        <DetailField label="Sub District" value={member.sub_district} />
                        
                        <DetailField label="Zone" value={member.zone} />
                        <DetailField label="Area Name" value={member.areaname} />
                        <DetailField label="Taluk" value={member.taluk} />
                        <DetailField label="Village" value={member.village} />
                        
                        <DetailField label="Ward" value={member.ward} />
                        <DetailField label="Pin Code" value={member.pin_code} />
                        <DetailField label="Police Station" value={member.police_station} />
                    </div>
                </section>

                {/* 5. Personal Details */}
                <section>
                    <h3 className="text-lg font-bold uppercase text-blue-700 mb-4 border-b pb-2">Personal & Identity Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <DetailField label="Gender" value={member.gender} />
                        <DetailField label="Date of Birth" value={formatDateToDDMMYYYY(member.date_of_birth)} />
                        <DetailField label="Religion" value={member.religion} />
                        
                        <DetailField label="Voter ID" value={member.voter_id} />
                        <DetailField label="Aadhar Number" value={member.aadhar_number} />
                        <DetailField label="Occupation" value={member.occupation} />
                        
                        <DetailField label="Business" value={member.business} />
                        <DetailField label="Business Designation" value={member.business_designation} />
                    </div>
                </section>

                {/* 6. Referral Information */}
                <section>
                    <h3 className="text-lg font-bold uppercase text-blue-700 mb-4 border-b pb-2">Referral Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <DetailField label="Referrer Name" value={member.referrer_name} />
                        <DetailField label="Referrer Mobile" value={member.referrer_mobile_number} />
                    </div>
                </section>

                {/* 7. Other */}
                <section>
                    <h3 className="text-lg font-bold uppercase text-blue-700 mb-4 border-b pb-2">Other Information</h3>
                    <div className="grid grid-cols-1 gap-6">
                        <DetailField label="Remarks" value={member.remarks} />
                        <div>
                        </div>
                    </div>
                </section>

                {/* 8. Payment History */}
                <section>
                    <h3 className="text-lg font-bold uppercase text-blue-700 mb-4 border-b pb-2">Payment History</h3>
                    <div className="overflow-x-auto border rounded-md shadow-sm">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fee Type</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Type</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Paid</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance Due</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {member.payments && member.payments.length > 0 ? (
                                    member.payments.map((payment) => (
                                        <tr key={payment.id}>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">{formatPaymentType(payment.payment_type)}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                {payment.payment_status_type === 'part' ? (
                                                    <span className="px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded bg-amber-100 text-amber-800">
                                                        Part Payment
                                                    </span>
                                                ) : (
                                                    <span className="px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded bg-green-100 text-green-800">
                                                        Full Payment
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">₹{payment.amount}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                {payment.payment_status_type === 'part' && payment.balance_amount ? (
                                                    <span className="text-red-600 font-semibold">₹{payment.balance_amount}</span>
                                                ) : '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDateToDDMMYYYY(payment.payment_date)}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{payment.payment_mode || '-'}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                    payment.status === 'success' ? 'bg-green-100 text-green-800' : 
                                                    payment.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                    {payment.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-center">No payment history found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

            </div>
        </div>
    );
};

export default MemberDetails;
